//components/ThemeSwitcher.tsx
'use client';

import * as React from 'react';
import { Leaf, Moon, Sun, Palette } from 'lucide-react';
import { useTheme } from '@/lib/theme-context';
import { themes, type Theme } from '@/lib/themes';
import { cn } from '@/lib/utils';

const THEME_META: Record<Theme, { icon: React.ComponentType<{ className?: string }>; label: string }> = {
  default: { icon: Leaf, label: 'Default' },
  light: { icon: Sun, label: 'Light' },
  dark: { icon: Moon, label: 'Dark' },
};

interface ThemeSwitcherProps {
  onThemeChange?: () => void;
}

/**
 * A single icon button that fans its options out in an arc, rather than a
 * 140px-wide dropdown permanently parked in the sidebar.
 *
 * The arc sweeps up and to the right because this sits at the bottom-left of
 * the sidebar — anywhere else and the options would open off-screen.
 */
export default function ThemeSwitcher({ onThemeChange }: ThemeSwitcherProps) {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (t: Theme) => {
    setTheme(t);
    setOpen(false);
    onThemeChange?.();
  };

  const Current = THEME_META[theme]?.icon ?? Palette;
  const RADIUS = 56;

  /**
   * The arc was hardcoded up-and-right, which suits the sidebar (bottom-left)
   * and puts the options off-screen in the signed-out navbar (top-right).
   * Measure the trigger and open into whichever quadrant has room.
   */
  const [dir, setDir] = React.useState({ x: 1, y: -1 });
  React.useLayoutEffect(() => {
    if (!open) return;
    const r = rootRef.current?.getBoundingClientRect();
    if (!r) return;
    const need = RADIUS + 48;
    setDir({
      x: r.left < need ? 1 : window.innerWidth - r.right < need ? -1 : 1,
      y: r.top < need ? 1 : window.innerHeight - r.bottom < need ? -1 : -1,
    });
  }, [open]);

  // Sweep from the vertical axis to the horizontal one, within that quadrant.
  const angleFor = (i: number, n: number) => {
    const vert = dir.y > 0 ? 90 : -90;
    const horiz = dir.x > 0 ? 0 : dir.y > 0 ? 180 : -180;
    const t = i / Math.max(1, n - 1);
    return (vert + (horiz - vert) * t) * (Math.PI / 180);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={`Theme: ${THEME_META[theme]?.label ?? theme}. Change theme`}
        title={`Theme: ${THEME_META[theme]?.label ?? theme}`}
        className={cn(
          'relative z-10 flex size-9 items-center justify-center rounded-full border transition-colors',
          'border-sidebar-foreground/25 bg-sidebar-foreground/10 text-sidebar-foreground',
          'hover:bg-sidebar-foreground/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
          open && 'bg-sidebar-foreground/20',
        )}
      >
        <Current className="size-[18px]" />
      </button>

      {/* Options are always mounted so they can animate, and are inert when closed. */}
      <div className={cn('pointer-events-none absolute inset-0', open && 'pointer-events-auto')}>
        {themes.map((t, i) => {
          const meta = THEME_META[t];
          const Icon = meta?.icon ?? Palette;
          const a = angleFor(i, themes.length);
          const x = open ? Math.cos(a) * RADIUS : 0;
          const y = open ? Math.sin(a) * RADIUS : 0;
          const active = t === theme;
          return (
            <button
              key={t}
              type="button"
              tabIndex={open ? 0 : -1}
              aria-hidden={!open}
              onClick={() => choose(t)}
              title={meta?.label ?? t}
              aria-label={meta?.label ?? t}
              style={{
                transform: `translate(${x}px, ${y}px) scale(${open ? 1 : 0.4})`,
                transitionDelay: `${open ? i * 35 : 0}ms`,
              }}
              className={cn(
                'absolute left-0 top-0 flex size-9 items-center justify-center rounded-full border shadow-sm',
                'transition-[transform,opacity] duration-200 ease-out',
                open ? 'opacity-100' : 'opacity-0',
                active
                  ? 'border-sidebar-ring bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'border-sidebar-foreground/25 bg-sidebar text-sidebar-foreground hover:bg-sidebar-foreground/15',
              )}
            >
              <Icon className="size-[17px]" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
